export const STOCK_BUCKET_KEYS = [
  'pendingInboundQty',
  'inboundTransitQty',
  'physicalQty',
  'pendingOutboundQty',
  'outboundTransitQty',
  'repairQty',
  'returnedQty',
  'damagedQty',
];

export const buildDefaultStockBuckets = () => ({
  pendingInboundQty: 0,
  inboundTransitQty: 0,
  physicalQty: 0,
  pendingOutboundQty: 0,
  outboundTransitQty: 0,
  repairQty: 0,
  returnedQty: 0,
  damagedQty: 0,
});

const toSafeNumber = (value) => {
  const numeric = Number(value || 0);
  return Number.isFinite(numeric) ? numeric : 0;
};

export const getPurchaseLineProgress = (line = {}) => {
  const orderedQuantity = toSafeNumber(line?.orderedQuantity);
  const cancelledQuantity = toSafeNumber(line?.cancelledQuantity);
  const orderedNet = Math.max(0, orderedQuantity - cancelledQuantity);
  const assignedQuantity = Math.max(0, toSafeNumber(line?.assignedQuantity));
  const pickedUpQuantity = Math.max(0, toSafeNumber(line?.pickedUpQuantity));
  const receivedQuantity = Math.max(0, toSafeNumber(line?.receivedQuantity));
  const pendingPickupQuantity = Math.max(0, toSafeNumber(line?.pendingPickupQuantity));

  return {
    orderedQuantity,
    cancelledQuantity,
    orderedNet,
    assignedQuantity,
    pickedUpQuantity,
    receivedQuantity,
    pendingPickupQuantity,
  };
};

export const computePurchaseLineStatus = (line = {}) => {
  const explicit = String(line?.status || '').trim();
  if (['repair', 'returned', 'damaged', 'cancelled'].includes(explicit)) return explicit;

  const {
    orderedNet,
    assignedQuantity,
    pickedUpQuantity,
    receivedQuantity,
    pendingPickupQuantity,
  } = getPurchaseLineProgress(line);

  if (orderedNet > 0 && receivedQuantity >= orderedNet) return explicit === 'stored' ? 'stored' : 'received';
  if (pickedUpQuantity > receivedQuantity) return 'inbound_in_transit';
  if (assignedQuantity > 0 || pendingPickupQuantity < orderedNet) {
    return pendingPickupQuantity > 0 ? 'partially_assigned' : 'assigned_to_route';
  }
  return explicit || 'pending_pickup';
};

export const computePurchaseHeaderStatus = (purchase = {}, lines = []) => {
  const normalizedLines = Array.isArray(lines) ? lines : [];
  if (!normalizedLines.length) return String(purchase?.status || 'pending_pickup');

  const statuses = normalizedLines.map((line) => computePurchaseLineStatus(line));

  if (statuses.every((status) => ['received', 'stored'].includes(status))) return 'received';
  if (statuses.some((status) => status === 'inbound_in_transit' || status === 'in_transit')) return 'in_transit';
  if (statuses.some((status) => status === 'assigned_to_route' || status === 'partially_assigned')) {
    return statuses.every((status) => status === 'assigned_to_route') ? 'assigned_to_route' : 'partially_assigned';
  }
  if (statuses.every((status) => status === 'cancelled')) return 'cancelled';

  return String(purchase?.status || 'pending_pickup');
};

export const buildPurchaseHeaderSummary = (purchase = {}, lines = []) => {
  const normalizedLines = Array.isArray(lines) ? lines : [];
  const completedLinesCount = normalizedLines.filter((line) => {
    const status = computePurchaseLineStatus(line);
    return status === 'received' || status === 'stored';
  }).length;
  const pendingLinesCount = Math.max(0, normalizedLines.length - completedLinesCount);
  const status = computePurchaseHeaderStatus(purchase, normalizedLines);

  return {
    status,
    completedLinesCount,
    pendingLinesCount,
    closedAt: pendingLinesCount === 0 && normalizedLines.length > 0 ? new Date() : null,
  };
};

export const getOutboundOrderLineProgress = (line = {}) => {
  const quantity = Math.max(0, toSafeNumber(line?.quantity));
  const cancelledQuantity = Math.max(0, toSafeNumber(line?.cancelledQuantity));
  const netQuantity = Math.max(0, quantity - cancelledQuantity);
  const assignedQuantity = Math.max(0, toSafeNumber(line?.assignedQuantity));
  const inTransitQuantity = Math.max(0, toSafeNumber(line?.inTransitQuantity));
  const deliveredQuantity = Math.max(0, toSafeNumber(line?.deliveredQuantity));
  const pendingDeliveryQuantity = Math.max(
    0,
    line?.pendingDeliveryQuantity != null
      ? toSafeNumber(line.pendingDeliveryQuantity)
      : netQuantity - assignedQuantity - inTransitQuantity - deliveredQuantity
  );

  return {
    quantity,
    cancelledQuantity,
    netQuantity,
    assignedQuantity,
    inTransitQuantity,
    deliveredQuantity,
    pendingDeliveryQuantity,
  };
};

export const computeOutboundOrderLineStatus = (line = {}) => {
  const explicit = String(line?.status || '').trim();
  if (['cancelled', 'returned', 'repair', 'damaged', 'in_warranty', 'ready_for_pickup'].includes(explicit)) {
    return explicit;
  }

  const {
    netQuantity,
    assignedQuantity,
    inTransitQuantity,
    deliveredQuantity,
    pendingDeliveryQuantity,
  } = getOutboundOrderLineProgress(line);

  if (netQuantity > 0 && deliveredQuantity >= netQuantity) return 'delivered';
  if (inTransitQuantity > 0) return deliveredQuantity > 0 ? 'partially_delivered' : 'in_transit';
  if (assignedQuantity > 0) {
    if (pendingDeliveryQuantity > 0) return 'partially_assigned';
    return 'assigned_to_route';
  }
  if (pendingDeliveryQuantity > 0) return explicit || 'ready_for_delivery';
  return explicit || 'draft';
};

export const computeOutboundOrderHeaderStatus = (order = {}, lines = []) => {
  const normalizedLines = Array.isArray(lines) ? lines : [];
  if (!normalizedLines.length) return String(order?.status || 'draft');

  const statuses = normalizedLines.map((line) => computeOutboundOrderLineStatus(line));

  if (statuses.every((status) => status === 'ready_for_pickup')) return 'ready_for_pickup';
  if (statuses.every((status) => status === 'in_warranty')) return 'in_warranty';
  if (statuses.every((status) => status === 'delivered')) return 'delivered';
  if (statuses.some((status) => status === 'partially_delivered')) return 'partially_delivered';
  if (statuses.some((status) => status === 'in_transit')) return 'in_transit';
  if (statuses.some((status) => status === 'partially_assigned')) return 'partially_assigned';
  if (statuses.every((status) => status === 'assigned_to_route')) return 'assigned_to_route';
  if (statuses.every((status) => status === 'cancelled')) return 'cancelled';
  if (statuses.some((status) => status === 'ready_for_delivery')) return 'ready_for_delivery';

  return String(order?.status || 'draft');
};

export const buildOutboundOrderHeaderSummary = (order = {}, lines = []) => {
  const normalizedLines = Array.isArray(lines) ? lines : [];
  const totals = normalizedLines.reduce(
    (acc, line) => {
      const progress = getOutboundOrderLineProgress(line);
      acc.quantity += progress.netQuantity;
      acc.pendingDeliveryQuantity += progress.pendingDeliveryQuantity;
      acc.assignedQuantity += progress.assignedQuantity;
      acc.inTransitQuantity += progress.inTransitQuantity;
      acc.deliveredQuantity += progress.deliveredQuantity;
      return acc;
    },
    {
      quantity: 0,
      pendingDeliveryQuantity: 0,
      assignedQuantity: 0,
      inTransitQuantity: 0,
      deliveredQuantity: 0,
    }
  );

  const status = computeOutboundOrderHeaderStatus(order, normalizedLines);
  return {
    status,
    totals,
    closedAt:
      ['delivered', 'cancelled'].includes(status) &&
      totals.pendingDeliveryQuantity === 0 &&
      totals.inTransitQuantity === 0 &&
      normalizedLines.length > 0
        ? new Date()
        : null,
  };
};
